"use client";

import Image from "next/image";
import Link from "next/link";
import { useRef, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Menu, X } from "lucide-react";
import { TopBar } from "../design-system/molecules/TopBar";
import AddressInput from "../design-system/molecules/AddressInput";
import { resolveLegacyLink } from "../utils/linkUtils";

type SubMenuItem = {
  id?: number | string;
  subText: string;
  subLink: string;
};

type MenuGroup = {
  id?: number | string;
  textItem: string;
  lienItem?: string;
  submenu?: SubMenuItem[];
};

type MainMenuItem = {
  id?: number | string;
  text: string;
  href?: string;
  menuItem?: MenuGroup[];
};

type MenuSuperieurItem = {
  id?: number | string;
  textItem: string;
  lienItem?: string;
  submenu?: SubMenuItem[];
};

type HeaderLogo = {
  logo?: {
    url?: string;
    alternativeText?: string;
  };
};

type HeaderProps = {
  menuSuperieur?: MenuSuperieurItem[];
  logo?: HeaderLogo;
  menu?: MainMenuItem[];
};
export default function Header({ menuSuperieur, logo, menu }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActiveMenu(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navItems: MainMenuItem[] = Array.isArray(menu) ? menu : [];

  const topMenuItems: MenuSuperieurItem[] = Array.isArray(menuSuperieur) ? menuSuperieur : [];

  return (
    <header className=" bg-white shadow-sm z-50">
      {/* TOP BAR */}
      <TopBar menuSuperieur={menuSuperieur} />

      {/* MAIN HEADER CONTENT */}
      <div
        className="lms-container py-3 flex justify-between  items-center gap-4">
        {/* LOGO */}
        <div className="flex items-center ">
          <Link href="/" className="flex items-center">
            {logo?.logo?.url && (
              <Image
                src={logo.logo.url}
                alt={logo.logo.alternativeText || "Les Menus Services"}
                width={120}
                height={120}
                className="w-[90px] sm:w-[110px] lg:w-[120px] h-auto"
              />
            )}
          </Link>
        </div>


        {/* DESKTOP MENU */}
        <div className="hidden lg:flex justify-center" ref={menuRef}>
          <nav className="flex items-center space-x-10">
            {navItems.map((item) => {
              const menuGroups = item.menuItem ?? [];
              const hasSubMenu = menuGroups.length > 0;
              // Timeout pour gestion du hover
              let timeoutId: ReturnType<typeof setTimeout> | null = null;

              const handleMouseEnter = () => {
                if (hasSubMenu) {
                  if (timeoutId) clearTimeout(timeoutId);
                  setActiveMenu(item.text);
                }
              };

              const handleMouseLeave = () => {
                if (hasSubMenu) {
                  timeoutId = setTimeout(() => {
                    setActiveMenu(null);
                  }, 250); // délai pour laisser le temps de passer sur le sous-menu
                }
              };

              return (
                <div
                  key={item.text}
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    onClick={() =>
                      hasSubMenu &&
                      setActiveMenu((prev) => (prev === item.text ? null : item.text))
                    }
                    className="flex items-center gap-1 font-medium text-lg transition hover:text-primaryGreen"
                  >
                    <Link href={resolveLegacyLink(item.href)}>{item.text}</Link>
                    {hasSubMenu &&
                      (activeMenu === item.text ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      ))}
                  </button>

                  {/* --- SUBMENU --- */}
                  {activeMenu === item.text && hasSubMenu && (
                    <div
                      className="absolute left-0 mt-3 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-72 z-50"
                      onMouseEnter={() => {
                        // annule la fermeture si on entre dans le sous-menu
                        if (timeoutId) clearTimeout(timeoutId);
                      }}
                      onMouseLeave={() => {
                        // ferme après délai quand on quitte le sous-menu
                        timeoutId = setTimeout(() => {
                          setActiveMenu(null);
                        }, 250);
                      }}
                    >
                      {menuGroups.map((group) => {
                        const subMenus = group.submenu ?? [];

                        return (
                          <div key={group.id ?? group.textItem} className="mb-3 last:mb-0">
                            <a
                              href={resolveLegacyLink(group.lienItem || "#")}
                              className="flex justify-between font-semibold bg-[#E8F5D2] px-3 py-1.5 rounded-md mb-1 hover:bg-[#dff0c0]"
                            >
                              {group.textItem}
                            </a>

                            {subMenus.length > 0 && (
                              <ul className="pl-3 border-l border-gray-300 space-y-1">
                                {subMenus.map((sub) => (
                                  <li key={sub.id ?? sub.subText}>
                                    <a
                                      href={resolveLegacyLink(sub.subLink || "#")}
                                      className="block px-2 py-1 rounded-md hover:bg-gray-50"
                                    >
                                      {sub.subText}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* ACTIONS: SEARCH + BURGER */}
        <div className="flex justify-end items-center gap-4">
          <div className="hidden md:block w-[240px] lg:w-[300px] ">
            <AddressInput />
          </div>

          <button
            className="lg:hidden p-2 rounded-lg border border-gray-300"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <nav className="flex flex-col px-6 py-4 space-y-4 text-lg">
            {/*  MENU SUPÉRIEUR EN VERSION MOBILE */}
            {topMenuItems.length > 0 && (
              <div className="flex flex-col space-y-3 pb-4">
                {topMenuItems.map((item) => {
                  const subMenus = item.submenu ?? [];

                  return (
                    <div key={item.id ?? item.textItem}>
                      <a
                        href={resolveLegacyLink(item.lienItem || "#")}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="font-semibold text-[17px] text-gray-700 hover:text-primaryGreen"
                      >
                        {item.textItem}
                      </a>

                      {subMenus.length > 0 && (
                        <ul className="pl-4 space-y-1 mt-1 border-l border-gray-300">
                          {subMenus.map((sub) => (
                            <li key={sub.id ?? sub.subText}>
                              <a
                                href={resolveLegacyLink(sub.subLink || "#")}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block text-[15px] text-gray-600 hover:text-primaryGreen"
                              >
                                {sub.subText}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {navItems.map((item) => {
              const menuGroups = item.menuItem ?? [];
              const hasSubMenu = menuGroups.length > 0;

              return (
                <div key={item.id ?? item.text}>
                  <button
                    onClick={() =>
                      hasSubMenu &&
                      setActiveMenu((prev) =>
                        prev === item.text ? null : item.text
                      )
                    }
                    className="flex justify-between items-center w-full font-medium"
                  >
                    <Link
                      href={resolveLegacyLink(item.href || "#")}
                      onClick={() => {
                        if (!hasSubMenu) setIsMobileMenuOpen(false);
                      }}
                    >
                      {item.text}
                    </Link>

                    {hasSubMenu &&
                      (activeMenu === item.text ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      ))}
                  </button>

                  {activeMenu === item.text && hasSubMenu && (
                    <div className="pl-4 mt-2 border-l border-gray-300 space-y-3">
                      {menuGroups.map((group) => {
                        const subMenus = group.submenu ?? [];

                        return (
                          <div key={group.id ?? group.textItem}>
                            <a
                              href={resolveLegacyLink(group.lienItem || "#")}
                              className="block bg-[#E8F5D2] px-3 py-1 rounded-md font-semibold hover:bg-[#dff0c0]"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {group.textItem}
                            </a>

                            {subMenus.length > 0 && (
                              <ul className="pl-2 space-y-1">
                                {subMenus.map((sub) => (
                                  <li key={sub.id ?? sub.subText}>
                                    <a
                                      href={resolveLegacyLink(sub.subLink || "#")}
                                      className="block px-2 py-1 rounded-md hover:bg-gray-50"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      {sub.subText}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="mt-4">
              <AddressInput />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
